import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteReportConfirmDialog, FilePreview, RichTextPreview } from '../components';
import { ERROR_CODES, REPORT_PLATFORM } from '../lib/constants';
import { useReportQuery } from '../lib/queries';
import { PATHS } from '../lib/paths';
import { HistoryDetails } from '../components/HistoryDetails';

export default function ReportPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const reportQuery = useReportQuery(params.id!);

  useEffect(() => {
    if (reportQuery.error) navigate(PATHS.errorPath(ERROR_CODES[404]));
  }, [navigate, reportQuery.error]);

  if (!reportQuery.data) return null;
  const { report } = reportQuery.data;

  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold col-span-2">{t('details.title')}</h1>

      <h3 className="col-span-2 font-bold text-title pt-10">{t('report.generalInfo')}</h3>
      <span className="font-semibold">{t('report.name')}</span>
      <span>{report.name}</span>

      <span className="font-semibold">{t('report.createdAt')}</span>
      <span className="grid">
        <span>{new Date(report.createdAt).toLocaleTimeString()}</span>
        <span>{new Date(report.createdAt).toLocaleDateString()}</span>
      </span>

      <span className="font-semibold">{t('report.env')}</span>
      <span>{t(`environment.${report.env}`)}</span>

      <span className="font-semibold">{t('report.platform')}</span>
      <span>{t(`platform.${report.platform}`)}</span>

      {report.platform === REPORT_PLATFORM.WEB && (
        <>
          <span className="font-semibold">{t('report.browsers')}</span>
          <span>{report.browsers?.join(', ')}</span>
        </>
      )}

      {report.platform === REPORT_PLATFORM.MOBILE && (
        <>
          <span className="font-semibold">{t('report.device')}</span>
          <span>{report.device}</span>

          <span className="font-semibold">{t('report.version')}</span>
          <span>{report.version}</span>
        </>
      )}

      <span className="font-semibold">{t('report.priority')}</span>
      <span>{t(`priority.${report.priority}`)}</span>

      <span className="font-semibold">{t('common.status')}</span>

      <span>{t(`report.status.${report.status}`)}</span>

      <h3 className="col-span-2 font-bold text-title pt-10">{t('report.currentBehavior')}</h3>

      <span className="font-semibold">{t('report.description')}</span>
      <span>{report.currentDescription}</span>

      <span className="font-semibold">{t('report.reproduce')}</span>
      <RichTextPreview value={report.reproduce} />

      {!!report.currentScreenshots?.length && (
        <>
          <span className="font-semibold">{t('report.screenshots')}</span>
          <FilePreview urls={report.currentScreenshots} />
        </>
      )}

      <h3 className="col-span-2 font-bold text-title pt-10">{t('report.expectedBehavior')}</h3>

      <span className="font-semibold">{t('report.description')}</span>
      <span>{report.expectedDescription}</span>

      {!!report.expectedScreenshots?.length && (
        <>
          <span className="font-semibold">{t('report.screenshots')}</span>
          <FilePreview urls={report.expectedScreenshots} />
        </>
      )}
      <DeleteReportConfirmDialog />
      {!!report.changeLogs?.length && <HistoryDetails changeLogs={report.changeLogs} />}
    </main>
  );
}
